import React, { useEffect, useMemo, useState } from 'react';
import { Segment } from 'tea-component';
import classNames from 'classnames';
import { useIntl } from 'umi';
import CommonBubble from '@home/components/CommonBubble';
import SvgIcon from '@home/components/SvgIcon';
import type { TKpiOverviewDataScope } from '../../utils/uaRoiUaMediaScopeFilters';
import '@/pages/dashboard/pages/monetization/SalesOverview/components/SegmentCo.scss';
import './index.scss';

/** 页面配置 pageConfig / props 中的文案字段（中英文由配置提供） */
export interface IKpiOverviewScopeTabsLabels {
  /** Tab「All」英文 */
  allTabLabelEn?: string;
  /** Tab「All」中文 */
  allTabLabelCn?: string;
  /** Tab「UA-ROI …」英文 */
  uaScopeTabLabelEn?: string;
  /** Tab「UA-ROI …」中文 */
  uaScopeTabLabelCn?: string;
  /** Tab2 旁 info 气泡英文 */
  uaScopeTipEn?: string;
  /** Tab2 旁 info 气泡中文 */
  uaScopeTipCn?: string;
}

export interface IKpiOverviewScopeTabsProps extends IKpiOverviewScopeTabsLabels {
  /** 默认数据口径 Tab */
  defaultScope?: TKpiOverviewDataScope;
  /** 与 RankListTabCo/CountriesContextTab 一致：当前口径从 data.kpiOverviewDataScope 读取 */
  data?: { kpiOverviewDataScope?: TKpiOverviewDataScope; [key: string]: unknown; };
  changeData?: (key: string, value: unknown) => void;
  className?: string;
  /** 透传到 tea Segment，默认与 SegmentCo / MetricsSegementCo 一致：`segment-light`（中文加 `segment-light-china`） */
  segmentClassName?: string;
  style?: React.CSSProperties;
  /** 由上层 Render 传入的子树 */
  children?: React.ReactNode;
  /** pageConfig 包裹层 */
  config?: { props?: Partial<IKpiOverviewScopeTabsProps>; };
}

const TAB_ALL: TKpiOverviewDataScope = 'all';
const TAB_UA: TKpiOverviewDataScope = 'ua_roi_ua_media';

/**
 * KPI 总览 UA 区块数据口径切换；文案由 pageConfig.props 配置。
 * 参考 CountriesContextTab（RankListTabCo）：口径取自 props.data，经 cloneElement 合并进子节点 data 下发。
 */
const KpiOverviewScopeTabs: React.FC<IKpiOverviewScopeTabsProps> = props => {
  const merged = { ...props, ...(props.config?.props || {}) };
  const {
    className,
    segmentClassName,
    style,
    children,
    data,
    changeData,
    defaultScope = TAB_ALL,
    allTabLabelEn = 'All',
    allTabLabelCn = '全部',
    uaScopeTabLabelEn = 'UA-ROI Campaign & UA Media Channel',
    uaScopeTabLabelCn = 'UA-ROI Campaign & UA 买量渠道',
    uaScopeTipEn = 'Data scope: Campaign type = UA-ROI and Media Channel = UA, subject to filters.',
    uaScopeTipCn = '数据口径：Campaign 类型为 UA-ROI，Media Channel 类型为 UA，受筛选器影响',
    ...resetProps
  } = merged;

  const [scope, setScope] = useState<TKpiOverviewDataScope>(
    () => data?.kpiOverviewDataScope ?? defaultScope,
  );

  useEffect(() => {
    const next = data?.kpiOverviewDataScope;
    if (next !== undefined) {
      setScope(next);
    }
  }, [data?.kpiOverviewDataScope]);

  const { locale } = useIntl();
  const isEn = locale === 'en-US';

  const tab2Tip = isEn ? uaScopeTipEn : uaScopeTipCn;
  const tab2Label = isEn ? uaScopeTabLabelEn : uaScopeTabLabelCn;

  const segmentOptions = useMemo(
    () => [
      {
        value: TAB_ALL,
        text: isEn ? allTabLabelEn : allTabLabelCn,
      },
      {
        value: TAB_UA,
        text: (
          <span className="dbd-kpi-overview-scope-tabs__tab2">
            <div className="dbd-segment-info">
              <span className="dbd-kpi-overview-scope-tabs__tab2-label">
                {tab2Label}
              </span>
              <CommonBubble
                trigger="hover"
                arrowPointAtCenter
                placement="top-start"
                content={tab2Tip}
                dark
              >
                <i>
                  <SvgIcon
                    className="dbd-segment-info-icon"
                    type={
                      scope === TAB_UA ? 'tab-tip-icon' : 'alert-circle-m'
                    }
                  />
                </i>
              </CommonBubble>
            </div>
          </span>
        ),
      },
    ],
    [
      isEn,
      scope,
      tab2Tip,
      tab2Label,
      allTabLabelEn,
      allTabLabelCn,
    ],
  );

  const handleScopeChange = (v: TKpiOverviewDataScope): void => {
    setScope(v);
    changeData?.('kpiOverviewDataScope', v);
  };

  return (
    <div
      className={classNames('dbd-kpi-overview-scope-tabs', className)}
      style={style}
    >
      <Segment
        value={scope}
        onChange={v => handleScopeChange(v as TKpiOverviewDataScope)}
        options={segmentOptions}
        className={classNames(
          'segment-light',
          !isEn && 'segment-light-china',
          'dbd-kpi-overview-scope-tabs__segment',
          segmentClassName,
        )}
      />
      <div className="dbd-kpi-overview-scope-tabs__body">
        {React.Children.map(children, child => {
          if (!React.isValidElement(child)) {
            return child;
          }
          const injectedProps: Record<string, unknown> = {
            ...resetProps,
            changeData,
            data: {
              ...(data || {}),
              kpiOverviewDataScope: scope,
            },
          };
          return React.cloneElement(child, injectedProps);
        })}
      </div>
    </div>
  );
};

export default KpiOverviewScopeTabs;
